import { useState, useEffect, useMemo } from "react";
import { toLocalISODate } from "@/lib/utils";
import { useParams, Link } from "react-router";
import ReactApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import PageMeta from "@/components/common/PageMeta";
import { useIsOwner, useIsViewer } from "@/hooks/useRole";
import FlockEconomicsCard from "./FlockEconomicsCard";
import FlockVaccinationCard from "./FlockVaccinationCard";
import { handleApiError } from "@/services/api";
import { flockReportService } from "@/services/flockReportService";
import { flockService } from "@/services/masterDataService";
import {
  standardCurveService,
  type StandardCurve,
  type CurvePoint,
} from "@/services/standardCurveService";
import {
  flockMetricsService,
  type FlockDailyMetrics,
} from "@/services/flockMetricsService";
import { curveValueAt, weeksFromDays } from "@/lib/curveLookup";
import SampledMeasureChart, { type SampleRow } from "./SampledMeasureChart";
import WeeklyPerformanceTable from "./WeeklyPerformanceTable";
import type {
  FlockDetailSummary,
  FlockProductionReport,
  FlockReportRow,
} from "@/services/flockReportService";

type RangePeriod = "2w" | "1m" | "all";

// The server caps a metrics listing at 500 and returns newest first. The
// default of 100 is about three months of a daily round sheet, which would
// silently drop the weekly body weights from the first half of a rearing —
// a growth curve starting at week 8 of 20 with nothing on screen to say so.
const METRICS_LIMIT = 500;

// Said once: three charts read the same capped listing, and three copies of
// this sentence is three chances for one of them to claim a different limit.
const TRUNCATED_NOTE = `Showing the most recent ${METRICS_LIMIT} readings — earlier ones aren't on this chart.`;

/**
 * Turn flock-daily-metric rows into age-indexed samples for one measure.
 *
 * Age is computed from the ARRIVAL DATE, not by looking the day up in the
 * production report: a weighing recorded on a day with no egg production row
 * would otherwise drop off the chart silently — which is most days for a
 * flock that is not laying yet, and those are exactly the flocks body weight
 * matters most for.
 */
function sampledRows(
  metrics: FlockDailyMetrics[],
  field: "avg_body_weight_g" | "avg_egg_weight_g",
  arrivalDate: string | undefined,
): SampleRow[] {
  const arrival = new Date(arrivalDate ?? "");
  if (Number.isNaN(arrival.getTime())) return [];
  return metrics
    .filter((m) => m[field] != null)
    .map((m) => {
      const d = new Date(m.date.slice(0, 10));
      return {
        date: m.date.slice(0, 10),
        ageDays: Math.round((d.getTime() - arrival.getTime()) / 86400000),
        actual: m[field] as number,
      };
    })
    .filter((r) => Number.isFinite(r.ageDays))
    .sort((a, b) => a.ageDays - b.ageDays);
}

// One shared empty array, not a fresh `[]` per render: the series memos below
// key on it, and minting a new one each time defeats them in exactly the case
// where there is nothing to recompute.
const NO_POINTS: CurvePoint[] = [];
const NO_ROWS: FlockReportRow[] = [];

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function InfoItem({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm font-semibold text-gray-800 dark:text-white/90">
        {typeof value === "number" ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

function KpiCard({
  label,
  value,
  unit,
  highlight,
}: {
  label: string;
  value: string | number;
  unit?: string;
  highlight?: boolean;
}) {
  return (
    <div className="card p-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p
        className={`mt-1 text-xl font-bold ${
          highlight
            ? "text-error-500 dark:text-error-400"
            : "text-gray-800 dark:text-white/90"
        }`}
      >
        {value}
        {unit && (
          <span className="ml-1 text-sm font-normal text-gray-500">{unit}</span>
        )}
      </p>
    </div>
  );
}

export default function FlockDetailPage() {
  const { id } = useParams<{ id: string }>();
  const flockId = Number(id);
  // Economics is money data — the endpoint is owner/viewer only. Call both
  // hooks unconditionally (no short-circuit) to satisfy rules-of-hooks.
  const isOwner = useIsOwner();
  const isViewer = useIsViewer();
  const canSeeEconomics = isOwner || isViewer;

  const [summary, setSummary] = useState<FlockDetailSummary | null>(null);
  const [report, setReport] = useState<FlockProductionReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [range, setRange] = useState<RangePeriod>("2w");
  // The breed standard and the flock's own measurements. Loaded SEPARATELY
  // from the report, and deliberately allowed to fail quietly: a benchmark is
  // an enhancement to this page, and a farm with no curves configured must
  // still see its production history rather than an error.
  const [hdpCurve, setHdpCurve] = useState<StandardCurve | null>(null);
  const [weightCurve, setWeightCurve] = useState<StandardCurve | null>(null);
  const [eggWeightCurve, setEggWeightCurve] = useState<StandardCurve | null>(null);
  const [feedIntakeCurve, setFeedIntakeCurve] = useState<StandardCurve | null>(null);
  const [metrics, setMetrics] = useState<FlockDailyMetrics[]>([]);
  // Tracked so the empty state can tell "nobody has weighed the birds" from
  // "the request failed". The first is a fact about the farm and names the fix;
  // the second is a fact about the network, and printing the first for it tells
  // the owner their staff are not doing a job they may well be doing.
  const [metricsFailed, setMetricsFailed] = useState(false);

  useEffect(() => {
    if (!flockId) return;
    setLoading(true);
    setError("");
    Promise.all([
      flockReportService.getFlockDetailSummary(flockId),
      flockReportService.getProductionReport(flockId),
    ])
      .then(([s, r]) => {
        setSummary(s);
        setReport(r);
      })
      // Surface the real failure instead of silently rendering the misleading
      // "Flock not found" for any transient/network error.
      .catch((err) => setError(handleApiError(err)))
      .finally(() => setLoading(false));
  }, [flockId]);

  // Which curve applies depends on the flock's BREED, and the detail summary
  // carries only the breed's name — so the flock itself is fetched for the id.
  // A name cannot be used: two farms can call a breed the same thing, and the
  // curve chain resolves on id.
  useEffect(() => {
    if (!flockId) return;
    let cancelled = false;
    // Reset per flock, like setLoading/setError in the effect above. Without
    // it, navigating from a flock whose metrics failed to one that loads fine
    // and has no weights renders "couldn't load" over a correctly-empty panel
    // — the same substitution, one flock over.
    setMetricsFailed(false);
    flockService
      .get(flockId)
      .then((flock) => {
        const breedId = flock.breed_id ?? null;
        // allSettled: a curve that 500s must not also discard the flock's own
        // measurements, which are the half of the chart that is about THIS
        // farm. Under Promise.all a missing standard blanked the readings too.
        return Promise.allSettled([
          standardCurveService.get("hdp", breedId),
          standardCurveService.get("body_weight", breedId),
          // egg_weight and feed_intake have been storable since migration
          // 000067 and nothing read them — CLAUDE.md #34 says so outright
          // ("the three are write-only so far"). This is the read side.
          standardCurveService.get("egg_weight", breedId),
          standardCurveService.get("feed_intake", breedId),
          flockMetricsService.list({ flock_id: flockId, limit: METRICS_LIMIT }),
        ]);
      })
      .then(([hdp, weight, eggWt, feedIn, rows]) => {
        if (cancelled) return;
        if (hdp.status === "fulfilled") setHdpCurve(hdp.value);
        if (weight.status === "fulfilled") setWeightCurve(weight.value);
        if (eggWt.status === "fulfilled") setEggWeightCurve(eggWt.value);
        if (feedIn.status === "fulfilled") setFeedIntakeCurve(feedIn.value);
        if (rows.status === "fulfilled") setMetrics(rows.value);
        else setMetricsFailed(true);
      })
      // Only reachable if the FLOCK lookup itself failed, which also means no
      // breed and so no curves. The page's own content does not depend on any
      // of this, but the body-weight panel must not then claim the farm has
      // recorded nothing.
      .catch(() => {
        if (!cancelled) setMetricsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [flockId]);

  // A curve is drawable exactly when it has points. That is the same thing as
  // `source !== "none"` — the server reports "none" precisely when it has
  // nothing to send — so this tests the one condition rather than two that
  // can drift apart. "none" is the NORMAL state for body weight: an averaged
  // body-weight curve is strain-specific to the point of being misleading, so
  // the app deliberately ships no default for it.
  const hdpPoints = hdpCurve?.points ?? NO_POINTS;
  // One flag, read by both the options memo and the series memo, so the axis
  // count and the series count cannot disagree.
  const hasHdpStandard = hdpPoints.length > 0;

  // Filter rows for recent charts based on range
  const recentRows = useMemo(() => {
    if (!report?.rows) return [];
    if (range === "all") return report.rows;
    const now = new Date();
    const days = range === "2w" ? 14 : 30;
    const cutoff = new Date(now);
    cutoff.setDate(cutoff.getDate() - days + 1);
    const cutoffStr = toLocalISODate(cutoff);
    return report.rows.filter((r) => r.date.slice(0, 10) >= cutoffStr);
  }, [report, range]);

  // Where the birds ARE decides whether there is production to show — not
  // flock_type, which a chick purchase sets to "layer" while the chicks are
  // still in the brooder (so HDP cards read 0% for day-olds), and which an
  // older flock typed "brooder" kept after it moved to a layer shed (so its
  // production was hidden for the rest of its life).
  const isLayer = summary?.shed_type !== "brooder";

  // Mortality chart
  const mortalityOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [0, 2] },
      plotOptions: { bar: { columnWidth: "60%" } },
      xaxis: {
        categories: recentRows.map((r) => r.date.slice(5)),
        labels: { style: { fontSize: "11px" }, rotate: -45, rotateAlways: recentRows.length > 14 },
        tickAmount: recentRows.length <= 14 ? undefined : Math.ceil(recentRows.length / 7),
      },
      yaxis: [
        { title: { text: "Deaths" }, min: 0, labels: { formatter: (v: number) => v.toFixed(0) } },
        {
          opposite: true,
          title: { text: "Bird Count" },
          labels: { formatter: (v: number) => v.toLocaleString() },
        },
      ],
      legend: { position: "top" },
      tooltip: { shared: true, intersect: false },
      colors: ["#b23a2e", "#35798c"],
      dataLabels: { enabled: false },
    }),
    [recentRows],
  );

  const mortalitySeries = useMemo(
    () => [
      { name: "Mortality", type: "bar" as const, data: recentRows.map((r) => r.mortality) },
      { name: "Bird Count", type: "line" as const, data: recentRows.map((r) => r.closing_birds) },
    ],
    [recentRows],
  );

  // Production chart
  const productionOptions: ApexOptions = useMemo(
    () => ({
      chart: { type: "line", toolbar: { show: false }, zoom: { enabled: false } },
      plotOptions: { bar: { columnWidth: "60%" } },
      xaxis: {
        categories: recentRows.map((r) => r.date.slice(5)),
        labels: { style: { fontSize: "11px" }, rotate: -45, rotateAlways: recentRows.length > 14 },
        tickAmount: recentRows.length <= 14 ? undefined : Math.ceil(recentRows.length / 7),
      },
      // The standard shares the HDP axis. It has to: two percentage scales
      // side by side would let the flock's line sit above the standard's while
      // being the lower number.
      yaxis: [
        {
          title: { text: "Trays" },
          min: 0,
          labels: { formatter: (v: number) => v.toFixed(0) },
        },
        {
          opposite: true,
          title: { text: "HDP %" },
          min: 0,
          max: 100,
          labels: { formatter: (v: number) => v.toFixed(1) + "%" },
        },
        // Only when the standard series is actually present. Apex maps
        // yaxis[i] to series[i], so a third axis config against two series is
        // a misalignment waiting to render something wrong — and the chart
        // stub in the tests publishes `series` only, so nothing would see it.
        ...(hasHdpStandard ? [{ show: false, min: 0, max: 100 }] : []),
      ],
      legend: { position: "top" },
      tooltip: { shared: true, intersect: false },
      // Dashed and grey, so the eye reads the standard as a reference rather
      // than as a third measurement of the farm.
      colors: hasHdpStandard
        ? ["#35798c", "#23734b", "#8a9199"]
        : ["#35798c", "#23734b"],
      stroke: hasHdpStandard
        ? { width: [0, 2, 2], dashArray: [0, 0, 5] }
        : { width: [0, 2] },
      dataLabels: {
        enabled: true,
        enabledOnSeries: [0],
        formatter: (v: number) => v.toFixed(0),
        style: { fontSize: "10px" },
      },
    }),
    [recentRows, hasHdpStandard],
  );

  const productionSeries = useMemo(() => {
    const base = [
      { name: "Trays", type: "bar" as const, data: recentRows.map((r) => r.production_trays) },
      { name: "HDP %", type: "line" as const, data: recentRows.map((r) => r.hdp_pct) },
    ];
    if (!hasHdpStandard) return base;
    return [
      ...base,
      {
        name: `Standard (${hdpCurve?.source})`,
        type: "line" as const,
        // age_days, not age_weeks: the report rounds weeks down, and reading
        // the curve at a whole week redraws it as a staircase.
        data: recentRows.map((r) => curveValueAt(hdpPoints, weeksFromDays(r.age_days))),
      },
    ];
  }, [recentRows, hdpPoints, hasHdpStandard, hdpCurve]);

  // Overall lifecycle chart (always full data)
  // NO_ROWS, not a fresh `[]`: the memos below key on this, and minting a new
  // array each render defeats them while the report is still loading. Same
  // reason as NO_POINTS at the top of the file.
  const lifecycleRows = report?.rows ?? NO_ROWS;
  const lifecycleOptions: ApexOptions = useMemo(() => {
    const count = lifecycleRows.length;
    const tickInterval = count <= 30 ? 1 : count <= 90 ? 7 : 30;
    return {
      chart: { type: "area", toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: [1, 2], curve: "smooth" },
      fill: {
        type: ["solid", "gradient"],
        opacity: [0.3, 0.8],
      },
      xaxis: {
        categories: lifecycleRows.map((r) => r.date.slice(5)),
        labels: { style: { fontSize: "10px" }, rotate: -45, rotateAlways: true },
        tickAmount: Math.ceil(count / tickInterval),
      },
      yaxis: [
        {
          title: { text: "Mortality" },
          min: 0,
          labels: { formatter: (v: number) => v.toFixed(0) },
        },
        {
          opposite: true,
          title: { text: "HDP %" },
          min: 0,
          max: 100,
          labels: { formatter: (v: number) => v.toFixed(0) + "%" },
        },
      ],
      legend: { position: "top" },
      tooltip: { shared: true, intersect: false },
      colors: ["#b23a2e", "#35798c"],
      dataLabels: { enabled: false },
    };
  }, [lifecycleRows]);

  // --- Body weight against the breed standard ---------------------------
  //
  // Its own x-axis, NOT the daily one the other charts share. Body weight is a
  // weekly sample on nearly every farm, so plotting it on a daily axis draws
  // one point per seven and six gaps. The axis here is the days the flock was
  // actually WEIGHED, which is also why no interpolation is applied to the
  // flock's own line: every point on it is a real reading.
  //
  // The standard is read at each of those same ages, so a point and its
  // benchmark are vertically aligned and the gap between them is the answer to
  // "are we on curve?".
  const weightRows = useMemo(
    () => sampledRows(metrics, "avg_body_weight_g", report?.arrival_date ?? summary?.arrival_date),
    [metrics, report, summary],
  );

  // Egg weight, the same sampled shape as body weight. Its own axis for the
  // same reason: it is a tray weighed now and then, not a daily number.
  //
  // It is also the denominator of FCR, so a flock drifting below its egg-weight
  // curve is being read here before it shows up as a feed-conversion problem.
  const eggWeightRows = useMemo(
    () => sampledRows(metrics, "avg_egg_weight_g", report?.arrival_date ?? summary?.arrival_date),
    [metrics, report, summary],
  );

  // Feed intake is DAILY, not sampled — it comes from the production report's
  // own rows, where feed_per_bird_g is already computed. So it goes on the
  // daily rows, and the sampled-chart component is the wrong shape for it.
  //
  // Below-curve intake is the earliest warning a layer farm gets: birds go off
  // feed before they go off lay, and by the time production drops the week is
  // already lost.
  const feedIntakeRows = useMemo(() => {
    const arrival = new Date(report?.arrival_date ?? summary?.arrival_date ?? "");
    if (Number.isNaN(arrival.getTime())) return [];
    return (report?.rows ?? [])
      .filter((r) => r.feed_per_bird_g != null && r.feed_per_bird_g > 0)
      .map((r) => {
        const d = new Date(r.date.slice(0, 10));
        return {
          date: r.date.slice(0, 10),
          ageDays: Math.round((d.getTime() - arrival.getTime()) / 86400000),
          actual: r.feed_per_bird_g as number,
        };
      })
      .filter((r) => Number.isFinite(r.ageDays))
      .sort((a, b) => a.ageDays - b.ageDays);
  }, [report, summary]);

  // The listing is capped and ordered newest-first, so exactly METRICS_LIMIT
  // rows means there are probably older ones the chart is not showing. Said on
  // screen rather than assumed away: a growth curve that quietly starts
  // mid-rearing reads as a flock that was never weighed early.
  const metricsTruncated = metrics.length >= METRICS_LIMIT;

  const lifecycleSeries = useMemo(
    () => [
      { name: "Mortality", type: "bar" as const, data: lifecycleRows.map((r) => r.mortality) },
      { name: "HDP %", type: "area" as const, data: lifecycleRows.map((r) => r.hdp_pct) },
    ],
    [lifecycleRows],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500 dark:text-gray-400">
        Loading flock details...
      </div>
    );
  }

  if (error || !summary) {
    return (
      <>
        <PageMeta title="Flock Detail | Poultry Book" description="" />
        <div className="card p-6">
          <p className="text-error-500">{error || "Flock not found"}</p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageMeta
        title={`${summary.batch_name} | Poultry Book`}
        description="Flock detail report"
      />

      {/* Breadcrumb */}
      <div className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        <Link to="/flocks" className="hover:text-gray-700 dark:hover:text-gray-200">
          Flocks
        </Link>
        <span className="mx-2">/</span>
        <span className="text-gray-800 dark:text-white/90">{summary.batch_name}</span>
      </div>

      {/* Summary Header */}
      <div className="mb-6 card p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-800 dark:text-white/90">
              {summary.batch_name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {summary.breed_name && `${summary.breed_name} \u00B7 `}
              {summary.shed_code}
              {summary.shed_type && ` (${summary.shed_type} shed)`}
            </p>
          </div>
          <span
            className={`rounded-full px-3 py-1 text-xs font-medium ${
              summary.status === "active"
                ? "bg-success-50 text-success-600 dark:bg-success-500/10 dark:text-success-400"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            }`}
          >
            {summary.status}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <InfoItem label="Age (Weeks)" value={summary.age_weeks} />
          <InfoItem label="Bird Housing Date" value={formatDate(summary.arrival_date)} />
          {summary.shifting_date && (
            <InfoItem label="Shifting to Layer" value={formatDate(summary.shifting_date)} />
          )}
          {summary.egg_start_date && (
            <InfoItem label="Egg Start Date" value={formatDate(summary.egg_start_date)} />
          )}
          <InfoItem label="Expected Culling (100W)" value={formatDate(summary.expected_culling)} />
          <InfoItem
            label="Birds"
            value={`${summary.current_count.toLocaleString()} / ${summary.initial_count.toLocaleString()}`}
          />
        </div>
      </div>

      {canSeeEconomics && <FlockEconomicsCard flockId={flockId} />}

      {/* Vaccination sits above the KPIs: an overdue dose is the one thing on
          this page that needs doing today, not reading. */}
      <FlockVaccinationCard flockId={flockId} />

      {/* KPI Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
        {/* Mortality and Livability rest on the same BASIS — deaths over the
            starting count — so they are valid, or not, together. This tile
            used to render unconditionally beside a Livability that was hidden
            for an onboarded flock: one number declared untrustworthy and the
            other presented as fact, in the same grid.

            They are exact complements, from ONE number: mortality is
            100 − livability, both from the flock's cumulative deaths. They
            used to come from different queries — one date-bounded to the
            report, one not — so a mortality log dated outside that range made
            two tiles in this grid fail to add to 100.

            For an onboarded flock the starting count was typed at adoption
            while the deaths are only the ones this app saw, so neither is a
            ratio of comparable things. FlockEconomicsCard already suppresses
            mortality% on exactly this basis. */}
        {summary.livability_pct != null && (
          <KpiCard
            label="Total Mortality"
            value={summary.total_mortality_pct.toFixed(2) + "%"}
            highlight={summary.total_mortality_pct > 5}
          />
        )}
        {isLayer && (
          <KpiCard label="Avg 2-Week HDP" value={summary.avg_2_week_hdp_pct.toFixed(2) + "%"} />
        )}
        {isLayer && (
          <KpiCard label="Current HDP" value={summary.current_hdp_pct.toFixed(2) + "%"} />
        )}
        <KpiCard
          label="Avg Feed/Bird (2W)"
          value={summary.avg_2_week_feed_per_bird_g.toFixed(1)}
          unit="g"
        />
        {/* Per hen HOUSED, not per hen alive.
            Hen-day production flatters a flock that lost birds — fewer hens in
            the denominator, so the percentage climbs while the shed produces
            less. This asks the owner's question instead: for every bird I paid
            for and put in that shed, how many eggs have I actually had? */}
        {isLayer && summary.eggs_per_hen_housed != null && (
          <KpiCard
            label="Eggs / Hen Housed"
            value={summary.eggs_per_hen_housed.toFixed(1)}
          />
        )}
        {/* The same conversion as FCR, in the unit a feed salesman quotes and
            a farmer argues in. */}
        {isLayer && summary.feed_per_dozen_kg != null && (
          <KpiCard
            // "Cycle" in the label, because this includes the REARING feed.
            // A benchmark quoted for a laying flock is around 1.5-1.6 kg, and
            // a farmer reading a bare "1.9" against that concludes they are
            // losing badly when they are simply reading a different measure.
            label="Feed / Dozen (cycle)"
            value={summary.feed_per_dozen_kg.toFixed(2)}
            unit="kg"
          />
        )}
        {summary.livability_pct != null && (
          <KpiCard label="Livability" value={summary.livability_pct.toFixed(1) + "%"} />
        )}
      </div>

      {/* Date Range Selector */}
      <div className="mb-4 flex gap-1 rounded-lg border border-gray-200 p-0.5 dark:border-gray-700 w-fit">
        {([["2w", "2W"], ["1m", "1M"], ["all", "All"]] as const).map(([val, label]) => (
          <button
            key={val}
            onClick={() => setRange(val)}
            className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${
              range === val
                ? "bg-brand-500 text-white"
                : "text-gray-600 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Mortality Chart */}
      <div className="mb-6 card p-5 sm:p-6">
        <h3 className="mb-2 text-base font-semibold text-gray-800 dark:text-white/90">
          Mortality
        </h3>
        {recentRows.length > 0 ? (
          <ReactApexChart
            options={mortalityOptions}
            series={mortalitySeries}
            type="line"
            height={280}
          />
        ) : (
          <p className="py-10 text-center text-sm text-gray-400">No data for this period.</p>
        )}
      </div>

      {/* Production Chart (layers only) */}
      {isLayer && (
        <div className="mb-6 card p-5 sm:p-6">
          <h3 className="mb-2 text-base font-semibold text-gray-800 dark:text-white/90">
            Production
          </h3>
          {recentRows.length > 0 ? (
            <ReactApexChart
              options={productionOptions}
              series={productionSeries}
              type="line"
              height={280}
            />
          ) : (
            <p className="py-10 text-center text-sm text-gray-400">No data for this period.</p>
          )}
        </div>
      )}

      {/* Body weight against the breed standard.
          Shown for EVERY flock, layer or brooder — body weight matters most
          for birds that are not laying yet, where it is the only early signal
          there is. A pullet off its growth curve at week 12 is still
          correctable; it will not reach production for another six weeks. */}
      <SampledMeasureChart
        title="Body Weight vs Standard"
        rows={weightRows}
        curve={weightCurve}
        yTitle="Grams per bird"
        unit="g"
        noCurveNote="No body-weight curve for this breed — showing measurements only."
        emptyNote="No body weights recorded for this flock. Weigh a sample and enter it on the Measurements step of the Daily Report."
        truncatedNote={metricsTruncated ? TRUNCATED_NOTE : undefined}
        failed={metricsFailed}
      />

      {/* Egg weight against the breed standard.
          Layers only: a brooder flock has no eggs to weigh, and an empty chart
          on every rearing flock's page is noise that teaches people to skip
          the section.

          This is also the denominator of FCR — so a flock drifting under its
          egg-weight curve is visible here before it reads as a feed-conversion
          problem, which is the more expensive way to find out. */}
      {/* shed_type, not flock_type. CLAUDE.md's domain map is explicit that
          context-dependent UI keys on where the birds ARE: a layer-type flock
          still in the brooder at week 10 has no eggs to weigh, and showing it
          "Weigh a tray" is exactly the noise this gate exists to prevent. */}
      {isLayer && (
        <SampledMeasureChart
          title="Egg Weight vs Standard"
          rows={eggWeightRows}
          curve={eggWeightCurve}
          yTitle="Grams per egg"
          unit="g"
          decimals={1}
          noCurveNote="No egg-weight curve for this breed — showing measurements only."
          emptyNote="No egg weights recorded for this flock. Weigh a tray on the Measurements step of the Daily Report — it is also what FCR is calculated from."
          truncatedNote={metricsTruncated ? TRUNCATED_NOTE : undefined}
          failed={metricsFailed}
        />
      )}

      {/* Feed intake against the breed standard.
          DAILY, unlike the two above — it is derived from feed actually issued,
          which is recorded every day, so it needs no sampling and gets the
          full series.

          Birds go off feed BEFORE they go off lay. By the time production
          drops the week is already lost, which is what makes this the earliest
          warning on the page. */}
      <SampledMeasureChart
        title="Feed Intake vs Standard"
        rows={feedIntakeRows}
        curve={feedIntakeCurve}
        yTitle="Grams per bird per day"
        unit="g/bird/day"
        decimals={1}
        noCurveNote="No feed-intake curve for this breed — showing actual intake only."
        emptyNote="No feed recorded against this flock yet."
      />

      {/* The numbers behind the charts above.
          A chart shows the shape; the table gives the week and the gap, which
          is what somebody argues from with a feed salesman or a breeder rep. */}
      <WeeklyPerformanceTable flockId={flockId} isLayer={isLayer} />

      {/* Overall Flock Status (full lifecycle) */}
      {isLayer && lifecycleRows.length > 0 && (
        <div className="mb-6 card p-5 sm:p-6">
          <h3 className="mb-2 text-base font-semibold text-gray-800 dark:text-white/90">
            Overall Flock Status
          </h3>
          <ReactApexChart
            options={lifecycleOptions}
            series={lifecycleSeries}
            type="line"
            height={320}
          />
        </div>
      )}
    </>
  );
}
